import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchContactCounts } from 'src/services/client';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';

// Interface for the hook's return value
export interface UseContactCountsReturn {
  contactCounts: any;
  isLoadingCounts: boolean;
  countsError: string | null;
  fetchContactCountsFromAPI: (contactId: string, role: string) => void;
  clearCountsState: () => void;
}

export const useContactCounts = (): UseContactCountsReturn => {
  const [contactCounts, setContactCounts] = useState<any | null>(null);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [countsError, setCountsError] = useState<string | null>(null);
  
  const adminUser = useSelector(selectAuthenticatedUser);
  
  const fetchContactCountsFromAPI = async (
    contactId: string,
    role: string
  ): Promise<void> => {
    setIsLoadingCounts(true);
    setCountsError(null);
    
    try {
      const loginUserId = adminUser.id;
      // Assuming roles is an array and the first role is the one we need
      const loginUserRole = adminUser.roles?.[0]?.role?.name;
      
      if (!loginUserId || !loginUserRole) {
        throw new Error('Authenticated user details (ID or Role) are missing.');
      }
      
      const response: any = await fetchContactCounts(
        contactId,
        role,
        loginUserId,
        loginUserRole
      );
      
      // Handle potential response errors from the service layer wrapper
      if (response.error || response.isError) {
        throw new Error(response.error || 'Failed to fetch contact counts');
      }
      
      if (response.status === 200 || response.ok) {
        const responseData = await response.json();
        
        if (responseData.statusCode === 200) {
          setContactCounts(responseData.data);
        } else {
          // Handle API-specific non-200 status codes wrapped in a 200 response
          throw new Error(
            `API returned status code: ${responseData.statusCode}`
          );
        }
      } else {
        // Handle HTTP non-200 status codes
        throw new Error(`Request failed with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Failed to fetch contact counts:', err);
      const errorMessage = err.message || 'Failed to fetch contact counts';
      setCountsError(errorMessage);
      
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 8000,
        position: 'top-center',
      });
    } finally {
      setIsLoadingCounts(false);
    }
  };
  
  const clearCountsState = () => {
    setContactCounts(null);
    setCountsError(null);
  };
  
  return {
    contactCounts,
    isLoadingCounts,
    countsError,
    fetchContactCountsFromAPI,
    clearCountsState,
  };
};